import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Mail, Phone, Calendar, Users, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';

export default function InteractionsTab({ clientId, interactions, contacts, onUpdate }) {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    type: 'Appel',
    titre: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    contact_id: '',
    duree: '',
    lieu: '',
    participants: '',
    resultat: '',
    created_by: 'user@sesam.com'
  });

  const handleAdd = () => {
    setFormData({
      type: 'Appel',
      titre: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      contact_id: '',
      duree: '',
      lieu: '',
      participants: '',
      resultat: '',
      created_by: 'user@sesam.com'
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.titre || !formData.description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const payload = {
        ...formData,
        participants: formData.participants ? formData.participants.split(',').map(p => p.trim()) : [],
        duree: formData.duree ? parseInt(formData.duree) : null
      };

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/interactions`,
        payload
      );
      toast.success('Interaction ajoutée avec succès');
      setShowModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving interaction:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (interactionId) => {
    if (!window.confirm('Supprimer cette interaction ?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/interactions/${interactionId}`
      );
      toast.success('Interaction supprimée');
      onUpdate();
    } catch (error) {
      console.error('Error deleting interaction:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Email':
        return <Mail className="h-5 w-5 text-blue-600" />;
      case 'Appel':
        return <Phone className="h-5 w-5 text-green-600" />;
      case 'Réunion':
        return <Users className="h-5 w-5 text-purple-600" />;
      case 'Proposition':
        return <MessageCircle className="h-5 w-5 text-orange-600" />;
      default:
        return <Calendar className="h-5 w-5 text-gray-600" />;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      'Email': 'bg-blue-100 text-blue-700',
      'Appel': 'bg-green-100 text-green-700',
      'Réunion': 'bg-purple-100 text-purple-700',
      'Proposition': 'bg-orange-100 text-orange-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Historique des Interactions</h3>
        <Button onClick={handleAdd} className="gap-2">
          <Plus className="h-4 w-4" />
          Ajouter une Interaction
        </Button>
      </div>

      {interactions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-gray-500">
            Aucune interaction enregistrée.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Timeline */}
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            {interactions.map((interaction, index) => {
              const contact = contacts.find(c => c.id === interaction.contact_id);
              return (
                <div key={interaction.id} className="relative flex gap-4 mb-6">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-white border-4 border-gray-200 flex items-center justify-center z-10">
                      {getTypeIcon(interaction.type)}
                    </div>
                  </div>
                  <Card className="flex-1 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={getTypeBadge(interaction.type)}>
                              {interaction.type}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {new Date(interaction.date).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                          <h4 className="font-bold text-lg">{interaction.titre}</h4>
                          {contact && (
                            <p className="text-sm text-gray-600">
                              Avec: {contact.prenom} {contact.nom} ({contact.fonction})
                            </p>
                          )}
                        </div>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={() => handleDelete(interaction.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <p className="text-gray-700 mb-3">{interaction.description}</p>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        {interaction.duree && (
                          <span>🕒 Durée: {interaction.duree} min</span>
                        )}
                        {interaction.lieu && (
                          <span>📍 Lieu: {interaction.lieu}</span>
                        )}
                        {interaction.participants && interaction.participants.length > 0 && (
                          <span>👥 Participants: {interaction.participants.join(', ')}</span>
                        )}
                      </div>

                      {interaction.resultat && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm font-medium text-green-800 mb-1">Résultat / Feedback:</p>
                          <p className="text-sm text-green-700">{interaction.resultat}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal Ajout */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajouter une interaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type d'interaction *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(val) => setFormData({ ...formData, type: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Appel">Appel téléphonique</SelectItem>
                    <SelectItem value="Réunion">Réunion</SelectItem>
                    <SelectItem value="Proposition">Proposition commerciale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Titre *</Label>
              <Input
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                placeholder="Ex: Appel de suivi projet LED"
              />
            </div>

            <div>
              <Label>Contact associé (optionnel)</Label>
              <Select
                value={formData.contact_id}
                onValueChange={(val) => setFormData({ ...formData, contact_id: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un contact" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.prenom} {contact.nom} - {contact.fonction}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Détails de l'interaction..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Durée (minutes, optionnel)</Label>
                <Input
                  type="number"
                  value={formData.duree}
                  onChange={(e) => setFormData({ ...formData, duree: e.target.value })}
                  placeholder="30"
                />
              </div>
              <div>
                <Label>Lieu (optionnel)</Label>
                <Input
                  value={formData.lieu}
                  onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                  placeholder="Siège Orange, Conakry"
                />
              </div>
            </div>

            <div>
              <Label>Participants (optionnel, séparés par des virgules)</Label>
              <Input
                value={formData.participants}
                onChange={(e) => setFormData({ ...formData, participants: e.target.value })}
                placeholder="M. Diallo, A. Camara"
              />
            </div>

            <div>
              <Label>Résultat / Feedback (optionnel)</Label>
              <Textarea
                value={formData.resultat}
                onChange={(e) => setFormData({ ...formData, resultat: e.target.value })}
                placeholder="Client intéressé, demande devis détaillé..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSubmit}>
                Ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
